"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Types ───────────────────────────────────────────────────────────────────

interface Testimonial {
  name: string;
  source: "Play Store" | "App Store";
  date: string;
  rating: number;
  title?: string;
  quote: string;
}

// ── Data — real App Store / Google Play reviews ─────────────────────────────

const testimonials: Testimonial[] = [
  {
    name: "Mohammed Abdul Rehman",
    source: "Play Store",
    date: "02/08/2026",
    rating: 5,
    quote:
      "My experience with JustGold has been excellent from start to finish. The ability to buy gold digitally in small fractions and later redeem it as physical gold through a smooth delivery process is truly impressive. The app is simple, transparent, and easy to use. The trust, professionalism, and exceptional customer service provided throughout the journey make JustGold a reliable choice for gold investment. Highly recommended!",
  },
  {
    name: "Anmol Chawla",
    source: "Play Store",
    date: "08/07/2026",
    rating: 5,
    quote:
      "JustGold has made investing in real 24K gold incredibly easy and accessible here in Dubai. The app is user-friendly with live market prices and buying/selling is instant 24/7. What stands out is that my gold is fully backed by physical assets, securely vaulted, regulated, insured. The option to redeem for physical delivery to my doorstep is a game-changer. Seamless registration, secure transactions, and transparent everything — exactly what I was looking for in a modern gold savings platform.",
  },
  {
    name: "Syed hannaan ullah hussaini",
    source: "Play Store",
    date: "08/04/2026",
    rating: 5,
    quote:
      "A very reliable and smooth app experience. Navigation is simple, and everything is transparent. Even during system outages, my gold was 100% safe. Not even 0.01 gram was affected. That level of security builds real trust.",
  },
  {
    name: "Dr Swamy Nadh",
    source: "Play Store",
    date: "28/11/2025",
    rating: 5,
    quote:
      "I have completed over ten transactions on Just Gold, including buying, selling and gold delivery. Every experience has been smooth, transparent and reliable. My delivery arrived sealed and certified, which gave me full confidence in the platform. The app is simple, pricing is fair, and the support team is excellent. Just Gold has earned my trust. Highly recommended.",
  },
  {
    name: "Bovas John",
    source: "Play Store",
    date: "02/01/2026",
    rating: 5,
    title: "Great UI, Easy to Use",
    quote:
      "This app was my top choice for saving money in the UAE. It's easy to get started and clearly shows how my investments grow every day. Highly recommended for anyone looking to start with small savings.",
  },
  {
    name: "Kainat Javed",
    source: "App Store",
    date: "28 Oct",
    rating: 5,
    title: "Trusted and always available for support",
    quote:
      "Very good app for saving and always available for support! I received my 2gm the next day! Do put your trust on it for saving!",
  },
  {
    name: "FarhanZubairi",
    source: "App Store",
    date: "14 Jul",
    rating: 5,
    title: "Realtor & Investor",
    quote:
      "Prices, deliveries and service - everything is exceptional. Best part is they provide dedicated service agents to key accounts offering amazing prices, the team is available 24x7 for booking live. The Founder is an interesting guy who gains trust in the first go!",
  },
  {
    name: "Malghorani",
    source: "App Store",
    date: "1y ago",
    rating: 5,
    title: "Finally a simple and halal gold purchasing app in the Middle East",
    quote:
      "I just downloaded the app and I love how easy it is go through and do the basic functions. The price of gold is transparent and the option to pay with Bank transfer to save transaction fees is a savior tbh.",
  },
];

// Duplicate for infinite scroll
const duplicatedTestimonials = [...testimonials, ...testimonials];

// ── Motion ──────────────────────────────────────────────────────────────────

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

// ── Star Rating ─────────────────────────────────────────────────────────────

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star
          key={i}
          className="h-4 w-4 fill-accent-500 text-accent-500"
        />
      ))}
    </div>
  );
}

// ── Testimonial Card ────────────────────────────────────────────────────────

function TestimonialCard({
  testimonial,
  ariaHidden = false,
}: {
  testimonial: Testimonial;
  ariaHidden?: boolean;
}) {
  return (
    <div
      aria-hidden={ariaHidden}
      className={cn(
        "flex w-[82vw] min-w-[82vw] max-w-[380px] shrink-0 select-none flex-col gap-3 rounded-card border border-[#e3ecfb] bg-white p-6 md:w-auto md:min-w-[320px] md:max-w-[380px]"
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <StarRating count={testimonial.rating} />
        <span className="text-xs text-text-muted">{testimonial.date}</span>
      </div>
      {testimonial.title ? (
        <p className="text-sm font-bold leading-snug text-text-heading">
          {testimonial.title}
        </p>
      ) : null}
      <p className="line-clamp-6 text-sm leading-relaxed text-text-body">
        {testimonial.quote}
      </p>
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-[#e3ecfb] pt-3">
        <p className="text-sm font-semibold text-text-heading">
          {testimonial.name}
        </p>
        <p className="text-xs text-text-muted">{testimonial.source}</p>
      </div>
    </div>
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

export function TestimonialsSection() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="border-t border-[#e3ecfb] bg-[#f7faff] pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
    >
      {/* Inline keyframes for the infinite scroll */}
      <style>{`
        @keyframes testimonial-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(calc(-50% - 0.75rem)); }
        }
      `}</style>

      <motion.div
        variants={container}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="mx-auto max-w-[1440px] px-5 md:px-8"
      >
        {/* Header */}
        <motion.div
          variants={fadeUp(!!reduce)}
          className="mx-auto mb-14 max-w-2xl text-center"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-primary-500">
            Real reviews
          </span>
          <h2 className="mt-3 text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900">
            What our users say
          </h2>
          <p className="mt-4 text-base leading-relaxed text-text-body md:text-lg">
            Real reviews from the App Store and Google Play.
          </p>
        </motion.div>

        {/* Continuous carousel on every viewport. The duplicated track lets the
            loop restart without exposing an empty gap. */}
        <motion.div
          variants={fadeUp(!!reduce)}
          className="group overflow-hidden"
          aria-label="Customer testimonials"
        >
          <div
            className="flex w-max gap-6 group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused] group-active:[animation-play-state:paused]"
            style={{
              animation: reduce
                ? "none"
                : "testimonial-scroll 110s linear infinite",
            }}
          >
            {duplicatedTestimonials.map((t, i) => (
              <TestimonialCard
                key={`${t.name}-${i}`}
                testimonial={t}
                ariaHidden={i >= testimonials.length}
              />
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
